import type { LayoutElement, LayoutProject } from '../types';
import { APP_VERSION } from '../constants';
import { EQUIPMENT_LAYER_VERSION } from '../features/equipment/equipmentConstants';
import type {
  EquipmentDoc,
  EquipmentInstance,
  EquipmentLayerData,
  EquipmentTemplate,
} from '../features/equipment/equipmentTypes';
import {
  equipmentProvenance,
  equipmentReviewStatus,
  isDxfDerivedEquipment,
} from './equipmentGeometry';
import { isLayoutEquipmentElement } from './layout3d';
import { buildEquipmentDoc, isEquipmentDoc } from './equipmentStorage';
import {
  EQUIPMENT_OVERLAY_PROP,
  equipmentInstanceFromLayoutElement,
  layoutElementFromEquipmentInstance,
} from './stockerExport';

/** Combined fab export: AMHS layout elements + semantic equipment overlay. */
export type FabProject = LayoutProject & {
  kind: 'fab-project';
  equipment?: EquipmentLayerData;
};

export type EquipmentUiPrefs = {
  visible?: boolean;
  showLabels?: boolean;
  showPorts?: boolean;
};

export function buildEquipmentLayerData(
  templates: EquipmentTemplate[],
  instances: EquipmentInstance[],
  ui?: EquipmentUiPrefs
): EquipmentLayerData | undefined {
  const saveInstances = instances.map(promoteAcceptedDxfToLayoutSaved);
  const referencedTemplateIds = new Set(
    saveInstances.map((inst) => inst.templateId).filter((id): id is string => Boolean(id))
  );
  // Potential/rejected instances are filtered before this function. Do not let
  // their auto-generated library templates leak into a save on their own.
  const exportTemplates = templates.filter(
    (template) =>
      template.metadata?.source !== 'auto-extraction' || referencedTemplateIds.has(template.id)
  );
  if (exportTemplates.length === 0 && saveInstances.length === 0) return undefined;
  return {
    version: EQUIPMENT_LAYER_VERSION,
    templates: exportTemplates,
    instances: saveInstances,
    ...(ui?.visible !== undefined ? { visible: ui.visible } : {}),
    ...(ui?.showLabels !== undefined ? { showLabels: ui.showLabels } : {}),
    ...(ui?.showPorts !== undefined ? { showPorts: ui.showPorts } : {}),
  };
}

export type LayoutSaveProjectParams = {
  layoutBase: LayoutProject;
  templates: EquipmentTemplate[];
  instances: EquipmentInstance[];
  /** Unused. Overlays are saved only under `equipment`, not copied into `elements`. */
  markedIds: string[];
  floorPlanId?: string | null;
  floorPlanName?: string | null;
  equipmentUi?: EquipmentUiPrefs;
};

/**
 * Build a layout export with tracks in `elements` and overlays in `equipment`.
 * Overlay instances are not duplicated as layout elements.
 */
export function buildLayoutSaveProject(params: LayoutSaveProjectParams): FabProject {
  const baseProject = buildFabProject(
    params.layoutBase,
    params.templates,
    params.instances,
    {
      floorPlanName: params.floorPlanName,
      equipmentUi: params.equipmentUi,
    }
  );
  return omitCopiedEquipmentFromElements(baseProject);
}

/** Merge layout project fields with the equipment identification layer. */
export function buildFabProject(
  layout: LayoutProject,
  templates: EquipmentTemplate[],
  instances: EquipmentInstance[],
  options?: { floorPlanName?: string | null; equipmentUi?: EquipmentUiPrefs }
): FabProject {
  const equipment = buildEquipmentLayerData(templates, instances, options?.equipmentUi);
  return {
    ...layout,
    kind: 'fab-project',
    metadata: {
      ...layout.metadata,
      updatedAt: new Date().toISOString(),
      ...(options?.floorPlanName !== undefined
        ? { floorPlanName: options.floorPlanName }
        : {}),
    },
    ...(equipment ? { equipment } : {}),
  };
}

/**
 * Drop overlay copies that used to be flattened into `elements`, and the legacy
 * top-level `stockers` block. Equipment lives only under `project.equipment`.
 */
export function omitCopiedEquipmentFromElements(project: FabProject): FabProject {
  const overlayIds = (project.equipment?.instances ?? []).map((inst) => inst.id);
  const { elements, order } = stripOverlayDuplicateElements(
    project.elements ?? {},
    project.elementOrder ?? Object.keys(project.elements ?? {}),
    overlayIds
  );
  const { stockers: _removed, ...rest } = project;
  return {
    ...rest,
    elements,
    elementOrder: order,
  };
}

/**
 * @deprecated Overlays are no longer copied into `elements`. Strips copies instead.
 */
export function withMarkedStockers(
  project: FabProject,
  _params: {
    equipmentInstances: EquipmentInstance[];
    markedIds: string[];
    floorPlanId?: string | null;
    floorPlanName?: string | null;
  }
): FabProject {
  return omitCopiedEquipmentFromElements(project);
}

/**
 * Marked overlay IDs from a saved layout:
 * - layout elements with `customProperties.source` of `dxf-insert` or `dxf-equipment`
 * - legacy: top-level `stockers` block from older exports
 */
export function extractStockerMarkIds(project: unknown): string[] {
  if (!project || typeof project !== 'object') return [];
  const p = project as LayoutProject;
  const ids: string[] = [];

  for (const el of Object.values(p.elements ?? {})) {
    if (!el) continue;
    const props = el.customProperties ?? {};
    const source = props.source;
    if (source === 'dxf-insert' || source === 'dxf-equipment') {
      const dxfId = props.dxfEquipmentId;
      ids.push(typeof dxfId === 'string' && dxfId ? dxfId : el.id);
    }
  }

  if (p.stockers && Array.isArray(p.stockers.stockers)) {
    for (const s of p.stockers.stockers) {
      if (typeof s?.id === 'string' && s.id) ids.push(s.id);
    }
  }

  return ids.filter((id, index, arr) => arr.indexOf(id) === index);
}

export function extractEquipmentLayer(project: unknown): EquipmentLayerData | null {
  if (!project || typeof project !== 'object') return null;
  const equipment = (project as { equipment?: EquipmentLayerData }).equipment;
  return equipment ?? null;
}

/**
 * Rebuild equipment overlays from layout elements that were flattened from the
 * marked save list. Used when the backend drops the top-level `equipment` field.
 */
export function rebuildEquipmentLayerFromLayoutElements(
  elements: Record<string, LayoutElement> | undefined | null
): EquipmentLayerData | null {
  if (!elements) return null;
  const instances: EquipmentInstance[] = [];
  for (const el of Object.values(elements)) {
    if (!el) continue;
    const inst = equipmentInstanceFromLayoutElement(el);
    if (inst) instances.push(inst);
  }
  if (instances.length === 0) return null;
  return {
    version: EQUIPMENT_LAYER_VERSION,
    templates: [],
    instances,
    visible: true,
    showPorts: false,
  };
}

function markAsLayoutSaved(inst: EquipmentInstance): EquipmentInstance {
  return {
    ...inst,
    metadata: {
      ...inst.metadata,
      restoredFromLayoutElement: true,
    },
  };
}

/**
 * A DXF footprint the user accepted and then saved is their data, not a
 * candidate. Persist it as layout equipment so it reloads confirmed: the
 * backend equipment schema drops the top-level `reviewStatus` field, while
 * anything under `metadata` round-trips.
 */
function promoteAcceptedDxfToLayoutSaved(inst: EquipmentInstance): EquipmentInstance {
  if (equipmentProvenance(inst) !== 'dxf') return inst;
  if (equipmentReviewStatus(inst) !== 'accepted') return inst;
  return markAsLayoutSaved(inst);
}

function markAsDxfEquipment(inst: EquipmentInstance): EquipmentInstance {
  const metadata = { ...inst.metadata };
  delete metadata.restoredFromLayoutElement;
  return { ...inst, metadata };
}

/**
 * True DXF extracts stay as wash overlays. Everything else in a fab-project
 * `equipment` catalog (manual identify / saved tools) is layout equipment.
 */
function shouldTreatEmbeddedAsLayoutSaved(inst: EquipmentInstance): boolean {
  return equipmentProvenance(inst) !== 'dxf';
}

/** True when any overlay was saved with the layout (layout-provenance instance). */
export function hasLayoutSavedEquipment(
  instances: Iterable<EquipmentInstance> | null | undefined
): boolean {
  if (!instances) return false;
  for (const inst of instances) {
    if (inst?.metadata?.restoredFromLayoutElement === true) return true;
  }
  return false;
}

/**
 * True when a load should paint overlays right away: layout-saved footprints or
 * manually identified equipment. Only untouched DXF extracts stay hidden until
 * the user enables Overlay / Identify Equipment.
 */
export function hasVisibleOnLoadEquipment(
  instances: Iterable<EquipmentInstance> | null | undefined
): boolean {
  if (!instances) return false;
  for (const inst of instances) {
    if (inst && equipmentProvenance(inst) !== 'dxf') return true;
  }
  return false;
}

/**
 * Equipment overlays for a layout load:
 * - Saved/marked items (from flattened elements) → Layout provenance
 * - Remaining embedded catalog keeps its own provenance (manual or DXF)
 * Layout-saved and manually identified footprints show immediately; untouched
 * DXF extracts stay hidden until Overlay / Identify Equipment / Generate Overlays.
 */
export function resolveEquipmentLayerForLoad(
  project: unknown,
  elements?: Record<string, LayoutElement> | null
): EquipmentLayerData | null {
  const els = elements ?? (project as LayoutProject | null)?.elements;
  const rebuilt = rebuildEquipmentLayerFromLayoutElements(els);
  const markIds = new Set(extractStockerMarkIds(project));
  const embedded = extractEquipmentLayer(project);

  const byId = new Map<string, EquipmentInstance>();

  // Full embedded catalog first (typical fab export / local JSON).
  if (embedded) {
    for (const inst of embedded.instances) {
      const asLayout =
        markIds.has(inst.id) || shouldTreatEmbeddedAsLayoutSaved(inst);
      byId.set(inst.id, asLayout ? markAsLayoutSaved(inst) : markAsDxfEquipment(inst));
    }
  }

  // Merge marked/flattened elements without allowing stale layout snapshots to
  // replace the embedded equipment catalog's canonical ports.
  if (rebuilt) {
    for (const inst of rebuilt.instances) {
      const prev = byId.get(inst.id);
      byId.set(
        inst.id,
        markAsLayoutSaved({
          ...(prev ?? inst),
          ...inst,
          overrides: {
            ...prev?.overrides,
            ...inst.overrides,
            ports: prev?.overrides?.ports?.length
              ? prev.overrides.ports
              : inst.overrides?.ports,
            style: inst.overrides?.style ?? prev?.overrides?.style,
          },
          metadata: {
            ...prev?.metadata,
            ...inst.metadata,
            restoredFromLayoutElement: true,
          },
        })
      );
      markIds.add(inst.id);
    }
  }

  if (byId.size === 0) return null;

  const kept = Array.from(byId.values());
  const templateIds = new Set(
    kept.map((i) => i.templateId).filter((id): id is string => typeof id === 'string' && id.length > 0)
  );
  const templates = (embedded?.templates ?? []).filter((t) => templateIds.has(t.id));

  return {
    version: embedded?.version ?? EQUIPMENT_LAYER_VERSION,
    templates,
    instances: kept,
    visible: embedded?.visible === true || hasVisibleOnLoadEquipment(kept),
    showPorts: embedded?.showPorts ?? rebuilt?.showPorts ?? false,
    showLabels: embedded?.showLabels ?? rebuilt?.showLabels,
  };
}

/**
 * Synchronize flattened layout equipment with the resolved equipment catalog.
 * This removes the historical split-brain where request building used instance
 * ports while 2D/3D rendering used different ports embedded on the element.
 */
export function synchronizeLayoutEquipmentPorts(
  elements: Record<string, LayoutElement>,
  instances: Iterable<EquipmentInstance> | null | undefined
): Record<string, LayoutElement> {
  if (!instances) return elements;
  const byId = new Map<string, EquipmentInstance>();
  for (const instance of instances) byId.set(instance.id, instance);

  let next: Record<string, LayoutElement> | null = null;
  for (const [elementId, element] of Object.entries(elements)) {
    if (!isLayoutEquipmentElement(element)) continue;
    const linkedId =
      typeof element.customProperties?.dxfEquipmentId === 'string' &&
      element.customProperties.dxfEquipmentId
        ? element.customProperties.dxfEquipmentId
        : elementId;
    const instance = byId.get(linkedId) ?? byId.get(elementId);
    const canonical = instance?.overrides?.ports;
    if (!instance || !canonical?.length) continue;

    const parentType = instance.objectType === 'stocker' ? 'stocker' : 'equipment';
    const ports = canonical.map((port) => ({
      ...port,
      parentId: instance.id,
      parentType,
    }));
    const currentSnapshot = element.customProperties?.[EQUIPMENT_OVERLAY_PROP];
    const snapshot =
      currentSnapshot && typeof currentSnapshot === 'object'
        ? { ...(currentSnapshot as Record<string, unknown>), ports }
        : currentSnapshot;
    if (!next) next = { ...elements };
    next[elementId] = {
      ...element,
      customProperties: {
        ...(element.customProperties ?? {}),
        ports,
        ...(snapshot ? { [EQUIPMENT_OVERLAY_PROP]: snapshot } : {}),
      },
    };
  }
  return next ?? elements;
}

/**
 * When the backend drops `equipment`, merge live DXF extracts already in the
 * editor with the saved-layout overlays rebuilt from elements.
 */
export function mergeLiveDxfEquipmentIntoLayer(
  layer: EquipmentLayerData | null,
  liveInstances: EquipmentInstance[],
  liveTemplates: EquipmentTemplate[] = []
): EquipmentLayerData | null {
  const byId = new Map<string, EquipmentInstance>();
  const templateById = new Map<string, EquipmentTemplate>();

  for (const t of layer?.templates ?? []) templateById.set(t.id, t);
  for (const t of liveTemplates) {
    if (!templateById.has(t.id)) templateById.set(t.id, t);
  }

  for (const inst of layer?.instances ?? []) {
    byId.set(inst.id, inst);
  }

  for (const inst of liveInstances) {
    if (!isDxfDerivedEquipment(inst)) continue;
    if (byId.has(inst.id)) continue;
    byId.set(inst.id, markAsDxfEquipment(inst));
  }

  if (byId.size === 0) return null;

  const instances = Array.from(byId.values());
  const usedTemplateIds = new Set(
    instances
      .map((i) => i.templateId)
      .filter((id): id is string => typeof id === 'string' && id.length > 0)
  );

  return {
    version: layer?.version ?? EQUIPMENT_LAYER_VERSION,
    templates: Array.from(templateById.values()).filter((t) => usedTemplateIds.has(t.id)),
    instances,
    visible: layer?.visible === true || hasVisibleOnLoadEquipment(instances),
    showPorts: layer?.showPorts ?? false,
    showLabels: layer?.showLabels,
  };
}

/** Drop layout elements that were restored as equipment overlays (avoids blue-box duplicates). */
export function stripOverlayDuplicateElements(
  elements: Record<string, LayoutElement>,
  order: string[],
  overlayInstanceIds: Iterable<string>
): { elements: Record<string, LayoutElement>; order: string[] } {
  const drop = new Set(overlayInstanceIds);
  if (drop.size === 0) return { elements, order };

  // Also drop elements whose dxfEquipmentId points at a restored overlay.
  for (const el of Object.values(elements)) {
    if (!el) continue;
    if (drop.has(el.id)) continue;
    const dxfId = el.customProperties?.dxfEquipmentId;
    if (typeof dxfId === 'string' && drop.has(dxfId)) {
      drop.add(el.id);
    }
  }

  const nextElements: Record<string, LayoutElement> = {};
  for (const [id, el] of Object.entries(elements)) {
    if (!drop.has(id)) nextElements[id] = el;
  }
  const nextOrder = order.filter((id) => Boolean(nextElements[id]));
  return { elements: nextElements, order: nextOrder };
}

/** Payload for `loadEquipment` from embedded or standalone equipment data. */
export function equipmentLoadPayload(layer: EquipmentLayerData) {
  return {
    templates: layer.templates,
    instances: layer.instances,
    visible: layer.visible ?? true,
    showLabels: layer.showLabels,
    showPorts: layer.showPorts ?? false,
  };
}

/**
 * Layout-load payload: show footprints when the layout includes saved/marked or
 * manually identified equipment; keep pure DXF catalogs hidden by default.
 */
export function equipmentLoadPayloadForLayout(layer: EquipmentLayerData) {
  return {
    ...equipmentLoadPayload(layer),
    visible: layer.visible ?? hasVisibleOnLoadEquipment(layer.instances),
    showPorts: false,
  };
}

/**
 * Merge layout-saved overlay instances into layout `elements` when the backend
 * dropped flattened equipment (so 2D/3D still show equipment by default).
 */
export function ensureLayoutElementsFromSavedEquipment(
  elements: Record<string, LayoutElement>,
  order: string[],
  instances: Iterable<EquipmentInstance> | null | undefined
): { elements: Record<string, LayoutElement>; order: string[] } {
  if (!instances) return { elements, order };

  const nextElements = { ...elements };
  const nextOrder = [...order];
  let changed = false;

  for (const inst of instances) {
    if (!inst || equipmentProvenance(inst) !== 'layout') continue;
    if (nextElements[inst.id]) continue;
    const el = layoutElementFromEquipmentInstance(inst);
    if (!el || nextElements[el.id]) continue;
    nextElements[el.id] = el;
    nextOrder.push(el.id);
    changed = true;
  }

  return changed ? { elements: nextElements, order: nextOrder } : { elements, order };
}

/**
 * Hide a non-equipment layout element only when a DXF/manual overlay paints over it.
 * Stockers / fab equipment in `elements` always stay visible after Load Layout.
 */
export function isLayoutElementHiddenByEquipmentOverlay(
  element: LayoutElement,
  instances: Record<string, EquipmentInstance> | null | undefined,
  overlaysVisible: boolean
): boolean {
  if (!overlaysVisible || !instances) return false;
  if (isLayoutEquipmentElement(element)) return false;
  const dxfId = element.customProperties?.dxfEquipmentId;
  const inst =
    instances[element.id] ??
    (typeof dxfId === 'string' && dxfId ? instances[dxfId] : undefined);
  if (!inst) return false;
  if (equipmentProvenance(inst) === 'layout') return false;
  return true;
}

/**
 * Force layers that own layout equipment to visible so Load Layout always shows them.
 * Also un-hides equipment elements that were saved with metadata.visible === false.
 */
export function ensureEquipmentOwningLayersVisible(
  layers: Record<string, LayerLike> | null | undefined,
  elements: Record<string, LayoutElement>,
  order: string[]
): {
  layers: Record<string, LayerLike> | null | undefined;
  elements: Record<string, LayoutElement>;
} {
  if (!layers) return { layers, elements };

  let nextLayers: Record<string, LayerLike> | null = null;
  let nextElements: Record<string, LayoutElement> | null = null;

  for (const id of order) {
    const el = (nextElements ?? elements)[id];
    if (!el || !isLayoutEquipmentElement(el)) continue;

    if (el.metadata?.visible === false) {
      if (!nextElements) nextElements = { ...elements };
      nextElements[id] = {
        ...el,
        metadata: { ...el.metadata, visible: true },
      };
    }

    const layerId = el.metadata?.layerId || el.layerId;
    if (!layerId) continue;
    const layer = (nextLayers ?? layers)[layerId];
    if (!layer || layer.visible !== false) continue;
    if (!nextLayers) nextLayers = { ...layers };
    nextLayers[layerId] = { ...layer, visible: true };
  }

  return {
    layers: nextLayers ?? layers,
    elements: nextElements ?? elements,
  };
}

type LayerLike = { visible?: boolean; [key: string]: unknown };

/** Convert embedded layer data to a standalone equipment.json document. */
export function equipmentLayerToDoc(
  layer: EquipmentLayerData,
  floorPlanName?: string | null
): EquipmentDoc {
  return buildEquipmentDoc(layer.templates, layer.instances, floorPlanName);
}

export function equipmentDocToLayer(doc: EquipmentDoc): EquipmentLayerData {
  return {
    version: doc.version,
    templates: doc.templates,
    instances: doc.instances,
  };
}

export function isFabProject(data: unknown): data is FabProject {
  if (!data || typeof data !== 'object') return false;
  const d = data as Partial<FabProject>;
  return d.kind === 'fab-project' && Boolean(d.version && d.elements);
}

/** Parse an imported JSON file into layout, equipment-only, or combined fab project. */
export function classifyProjectFile(data: unknown):
  | { type: 'equipment'; doc: EquipmentDoc }
  | { type: 'fab'; project: FabProject }
  | { type: 'layout'; project: LayoutProject } {
  if (isEquipmentDoc(data)) {
    return { type: 'equipment', doc: data };
  }
  if (isFabProject(data)) {
    return { type: 'fab', project: data };
  }
  const project = data as LayoutProject;
  if (!project.version || !project.elements) {
    throw new Error('Invalid project file structure');
  }
  if (project.equipment) {
    return { type: 'fab', project: { ...project, kind: 'fab-project' } };
  }
  return { type: 'layout', project };
}

/** Suggested download name for a combined export. */
export function fabExportFilename(floorPlanName?: string | null): string {
  if (floorPlanName) {
    const base = floorPlanName.replace(/\.[^.]+$/, '');
    return `${base}-fab`;
  }
  return 'fab-layout';
}

/** Build a layout project shell ready for export (shared by toolbar + shortcuts). */
export function createLayoutExportBase(params: {
  elements: LayoutProject['elements'];
  elementOrder?: string[];
  layers: LayoutProject['layers'];
  layerOrder?: string[];
  groups?: LayoutProject['groups'];
  viewport: LayoutProject['viewport'];
  gridSettings: LayoutProject['gridSettings'];
  simulation?: LayoutProject['simulation'];
  trackNodeAnnotations?: LayoutProject['trackNodeAnnotations'];
  name?: string;
}): LayoutProject {
  const now = new Date().toISOString();
  return {
    version: APP_VERSION,
    metadata: {
      name: params.name ?? 'Fab Layout',
      createdAt: now,
      updatedAt: now,
    },
    elements: params.elements,
    elementOrder: params.elementOrder,
    layers: params.layers,
    layerOrder: params.layerOrder,
    groups: params.groups,
    viewport: params.viewport,
    gridSettings: params.gridSettings,
    ...(params.simulation ? { simulation: params.simulation } : {}),
    ...(params.trackNodeAnnotations && params.trackNodeAnnotations.length > 0
      ? { trackNodeAnnotations: params.trackNodeAnnotations }
      : {}),
  };
}
